/**
 * PreviewMuteToggle — Iter 110 (Mar 2026).
 *
 * Tiny floating speaker pill that lives on top of a local-file `<video>`
 * preview (the ones bound to `URL.createObjectURL(file)`). Solves a P1
 * UX bug reported by the founder:
 *
 *   > "El audio no se escucha en la previsualización local, lo que
 *      confunde al barbero haciéndole creer que el video está roto."
 *
 * Behaviour (IG-parity):
 *   · Every preview `<video>` MUST start `muted` (iOS/Android autoplay
 *     compliance). This component owns that initial state.
 *   · A tap on the speaker icon toggles `videoElement.muted` AND tries
 *     to resume playback (the spec for unmute-via-gesture is "may
 *     resume play"). When the browser still refuses (rare on
 *     well-behaved browsers, common on iOS low-power mode), we surface
 *     a friendly Spanish toast instead of failing silently.
 *   · The mute toggle is LOCAL ONLY — it never mutates the file the
 *     parent dialog will POST to `/api/uploads/media`. The audio track
 *     in the uploaded MP4/MOV stays intact.
 *
 * Usage:
 *   <div className="relative">
 *     <video ref={videoRef} src={previewUrl} muted playsInline autoPlay loop />
 *     <PreviewMuteToggle videoRef={videoRef} />
 *   </div>
 *
 * Props:
 *   videoRef   { current: HTMLVideoElement | null }   REQUIRED
 *   className  string                                 optional positioning override
 *   testIdSuffix string                               unique suffix for the data-testid
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Volume2, VolumeX } from 'lucide-react';
import { toast } from 'sonner';
import { tr } from '@/contexts/LanguageContext';

export const PreviewMuteToggle = ({
  videoRef,
  className = 'absolute bottom-3 right-3 z-30',
  testIdSuffix = 'preview',
}) => {
  // Mirror the DOM mute state in React so the icon re-renders. The DOM
  // is the source of truth — we keep it in sync whenever the video
  // element flips through controls native to the browser as well.
  const [muted, setMuted] = useState(true);
  // Track whether we've already shown the autoplay-blocked toast so we
  // don't spam the user on repeated taps in low-power mode.
  const toastShownRef = useRef(false);

  // Whenever the underlying video element appears (e.g. dialog opens),
  // pin its initial state to `muted=true` and re-read the actual flag.
  useEffect(() => {
    const v = videoRef?.current;
    if (!v) return;
    // Don't blindly overwrite — only enforce the initial-mute contract
    // the very first time we see a fresh element (detected by reading
    // currentTime === 0 AND no prior gesture has flipped it).
    if (v.dataset._mutePinned !== '1') {
      v.muted = true;
      v.dataset._mutePinned = '1';
    }
    setMuted(!!v.muted);

    // Keep the local React state in sync if the element ever gets its
    // mute flipped via something else (devtools, future code path).
    const onVolumeChange = () => setMuted(!!v.muted);
    v.addEventListener('volumechange', onVolumeChange);
    return () => v.removeEventListener('volumechange', onVolumeChange);
  }, [videoRef]);

  const toggle = useCallback(async () => {
    const v = videoRef?.current;
    if (!v) return;
    const nextMuted = !v.muted;
    v.muted = nextMuted;
    setMuted(nextMuted);

    // When the user unmutes, also resume playback if it stalled — many
    // browsers pause a video whose source was just attached without a
    // gesture. This is the gesture; we can call .play() now.
    if (!nextMuted) {
      try {
        await v.play();
        toastShownRef.current = false; // reset after a successful unmute
      } catch {
        // Autoplay refused even after the gesture (iOS low-power, some
        // strict configs). Tell the user what to do instead of silently
        // re-muting.
        if (!toastShownRef.current) {
          toastShownRef.current = true;
          toast.info(
            tr("Toca el video para activar el sonido. Tu navegador bloqueó la primera reproducción con audio."),
            { duration: 4500 },
          );
        }
      }
    }
  }, [videoRef]);

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={muted ? tr("Activar sonido del video") : tr("Silenciar video")}
      aria-pressed={!muted}
      data-testid={`preview-mute-toggle-${testIdSuffix}`}
      className={`${className} w-10 h-10 inline-flex items-center justify-center rounded-full bg-black/55 backdrop-blur-md border border-white/15 text-white hover:bg-black/75 active:scale-95 transition-all`}
    >
      {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
    </button>
  );
};

export default PreviewMuteToggle;
